import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    Plus,
    Edit,
    Trash2,
    LogOut,
    Save,
    X,
    Eye,
    EyeOff,
    ArrowLeft,
    Filter,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/common/SEO";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

// 26 letras + un grupo W-Z para el final del alfabeto
const LETTER_OPTIONS = [
    "A", "B", "C", "D", "E", "F", "G", "H", "I", "J",
    "K", "L", "M", "N", "Ñ", "O", "P", "Q", "R", "S",
    "T", "U", "V", "W-Z",
];

const EMPTY = {
    term: "",
    definition: "",
    letter: "A",
    published: true,
    order: null,
};

export default function AdminGlosario() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [terms, setTerms] = useState([]);
    const [filter, setFilter] = useState(null);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(false);

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/glosario");
            setTerms(data);
        } catch {
            toast.error("No se pudo cargar el glosario.");
        }
    }, []);

    useEffect(() => {
        reload();
    }, [reload]);

    const filtered = useMemo(() => {
        if (!filter) return terms;
        return terms.filter((t) => t.letter === filter);
    }, [terms, filter]);

    const countsByLetter = useMemo(() => {
        const m = {};
        for (const t of terms) m[t.letter] = (m[t.letter] || 0) + 1;
        return m;
    }, [terms]);

    const usedLetters = useMemo(() => {
        return LETTER_OPTIONS.filter((l) => countsByLetter[l] > 0);
    }, [countsByLetter]);

    function startNew() {
        setForm({ ...EMPTY, letter: filter || "A" });
        setEditing("new");
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    function startEdit(term) {
        setForm({
            term: term.term,
            definition: term.definition,
            letter: term.letter,
            published: term.published,
            order: term.order,
        });
        setEditing(term);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    function cancel() {
        setEditing(null);
        setForm(EMPTY);
    }

    async function save(e) {
        e.preventDefault();
        setLoading(true);
        const payload = { ...form };
        if (payload.order === null || payload.order === "") {
            delete payload.order;
        } else {
            payload.order = Number(payload.order);
        }
        try {
            if (editing === "new") {
                await api.post("/admin/glosario", payload);
                toast.success("Término creado.");
            } else {
                await api.put(`/admin/glosario/${editing.id}`, payload);
                toast.success("Término actualizado.");
            }
            await reload();
            cancel();
        } catch (e) {
            toast.error(
                formatApiError(e.response?.data?.detail) || "Error al guardar.",
            );
        } finally {
            setLoading(false);
        }
    }

    async function remove(term) {
        if (
            !confirm(
                `¿Eliminar "${term.term}"? Esta definición dejará de aparecer en el sitio.`,
            )
        )
            return;
        try {
            await api.delete(`/admin/glosario/${term.id}`);
            toast.success("Término eliminado.");
            await reload();
        } catch {
            toast.error("Error al eliminar.");
        }
    }

    async function togglePublished(term) {
        try {
            await api.put(`/admin/glosario/${term.id}`, {
                published: !term.published,
            });
            toast.success(term.published ? "Despublicado." : "Publicado.");
            await reload();
        } catch {
            toast.error("Error al actualizar.");
        }
    }

    async function handleLogout() {
        await logout();
        navigate("/login");
    }

    return (
        <>
            <SEO
                title="Admin · Glosario"
                description="Panel administrativo del glosario"
                path="/admin/glosario"
            />
            <section className="py-12 md:py-16" data-testid="admin-glosario">
                <div className="container-x">
                    {/* Header */}
                    <div className="flex flex-wrap justify-between items-start gap-4 mb-12 pb-8 border-b border-[var(--line)]">
                        <div>
                            <Link
                                to="/admin"
                                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] hover:text-[var(--danger)] mb-4"
                                data-testid="admin-back-to-hub"
                            >
                                <ArrowLeft className="w-3 h-3" />
                                Volver al panel
                            </Link>
                            <p className="eyebrow mb-3">Departamento · Glosario</p>
                            <h1 className="font-display text-4xl md:text-6xl text-[var(--paper-2)] leading-[0.95]">
                                Glosario · Administración
                            </h1>
                            <p className="font-mono text-xs text-[var(--ink-muted)] mt-2">
                                Sesión: {user?.email} · {terms.length} términos
                                · {usedLetters.length} letras
                            </p>
                        </div>
                        <div className="flex gap-3 flex-wrap">
                            <button
                                onClick={startNew}
                                className="btn-brutal btn-brutal-danger"
                                data-testid="admin-new-term"
                            >
                                <Plus className="w-4 h-4" />
                                Nuevo término
                            </button>
                            <button
                                onClick={handleLogout}
                                className="btn-brutal btn-brutal-ghost"
                                data-testid="admin-logout"
                            >
                                <LogOut className="w-4 h-4" />
                                Salir
                            </button>
                        </div>
                    </div>

                    {/* Form */}
                    {editing && (
                        <form
                            onSubmit={save}
                            className="dossier mb-12"
                            data-testid="admin-form"
                        >
                            <div className="dossier-header">
                                <span>
                                    {editing === "new"
                                        ? "Nuevo término"
                                        : `Editando: ${editing.term}`}
                                </span>
                                <button
                                    type="button"
                                    onClick={cancel}
                                    className="text-[var(--ink-dim)] hover:text-[var(--danger)]"
                                    data-testid="admin-form-close"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="dossier-content space-y-6">
                                <div className="grid sm:grid-cols-12 gap-6">
                                    <div className="sm:col-span-7">
                                        <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] block mb-2">
                                            Término
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={form.term}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    term: e.target.value,
                                                })
                                            }
                                            className="input-dossier"
                                            data-testid="admin-form-term"
                                            placeholder="Ej: Adicción"
                                        />
                                    </div>
                                    <div className="sm:col-span-3">
                                        <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] block mb-2">
                                            Letra
                                        </label>
                                        <select
                                            value={form.letter}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    letter: e.target.value,
                                                })
                                            }
                                            className="input-dossier"
                                            data-testid="admin-form-letter"
                                        >
                                            {LETTER_OPTIONS.map((l) => (
                                                <option
                                                    key={l}
                                                    value={l}
                                                    style={{ background: "#111" }}
                                                >
                                                    {l}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] block mb-2">
                                            Orden
                                        </label>
                                        <input
                                            type="number"
                                            value={form.order ?? ""}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    order:
                                                        e.target.value === ""
                                                            ? null
                                                            : Number(
                                                                  e.target.value,
                                                              ),
                                                })
                                            }
                                            className="input-dossier"
                                            data-testid="admin-form-order"
                                            placeholder="auto"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] block mb-2">
                                        Definición
                                    </label>
                                    <textarea
                                        required
                                        rows={5}
                                        value={form.definition}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                definition: e.target.value,
                                            })
                                        }
                                        className="input-dossier resize-y"
                                        data-testid="admin-form-definition"
                                        placeholder="Definición sin maquillaje…"
                                    />
                                </div>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        id="published"
                                        checked={form.published}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                published: e.target.checked,
                                            })
                                        }
                                        className="w-4 h-4 accent-[var(--danger)]"
                                        data-testid="admin-form-published"
                                    />
                                    <label
                                        htmlFor="published"
                                        className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--paper-2)] cursor-pointer"
                                    >
                                        Publicado
                                    </label>
                                </div>
                                <div className="flex gap-3 pt-4 border-t border-dashed border-[var(--ink-dim)]">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="btn-brutal btn-brutal-danger disabled:opacity-60"
                                        data-testid="admin-form-save"
                                    >
                                        <Save className="w-4 h-4" />
                                        {loading ? "Guardando…" : "Guardar"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={cancel}
                                        className="btn-brutal btn-brutal-ghost"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}

                    {/* Filter chips */}
                    <div className="mb-8">
                        <div className="flex items-center gap-2 mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)]">
                            <Filter className="w-3 h-3" />
                            Filtrar por letra
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setFilter(null)}
                                className={`font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-2 border ${
                                    !filter
                                        ? "bg-[var(--paper-2)] text-black border-[var(--paper-2)]"
                                        : "border-[var(--line)] text-[var(--paper)] hover:border-[var(--danger)]"
                                }`}
                                data-testid="filter-all"
                            >
                                Todas · {terms.length}
                            </button>
                            {usedLetters.map((l) => (
                                <button
                                    key={l}
                                    onClick={() => setFilter(l)}
                                    className={`font-display text-base px-3 py-1.5 border ${
                                        filter === l
                                            ? "bg-[var(--danger)] text-[var(--paper-2)] border-[var(--danger)]"
                                            : "border-[var(--line)] text-[var(--paper)] hover:border-[var(--danger)]"
                                    }`}
                                    data-testid={`filter-${l}`}
                                >
                                    {l}
                                    <span className="font-mono text-[9px] text-[var(--ink-muted)] ml-1.5">
                                        {countsByLetter[l]}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Terms list */}
                    <div className="space-y-2" data-testid="admin-terms-list">
                        <p className="eyebrow mb-4">
                            {filtered.length}{" "}
                            {filtered.length === 1 ? "término" : "términos"}{" "}
                            {filter ? "· filtro activo" : "· total"}
                        </p>
                        {filtered.map((term) => (
                            <article
                                key={term.id}
                                className="border border-[var(--line)] bg-[#1a1a1a] p-5 hover:border-[var(--danger)] transition-colors"
                                data-testid={`admin-term-${term.id}`}
                            >
                                <div className="flex flex-wrap items-start gap-4">
                                    <div className="flex-shrink-0">
                                        <span className="font-display text-2xl text-[var(--danger)] inline-block w-10 text-center">
                                            {term.letter}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                                            <h3 className="font-display text-xl text-[var(--warning)]">
                                                {term.term}
                                            </h3>
                                            {!term.published && (
                                                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--warning)] border border-[var(--warning)] px-2">
                                                    Borrador
                                                </span>
                                            )}
                                            <span className="font-mono text-[10px] text-[var(--ink-dim)]">
                                                Orden: {term.order}
                                            </span>
                                        </div>
                                        <p className="font-mono text-xs text-[var(--ink-muted)] line-clamp-2">
                                            {term.definition}
                                        </p>
                                    </div>
                                    <div className="flex gap-2 flex-shrink-0">
                                        <button
                                            onClick={() => togglePublished(term)}
                                            className="p-2 border border-[var(--line)] text-[var(--paper-2)] hover:border-[var(--warning)] hover:text-[var(--warning)]"
                                            title={term.published ? "Despublicar" : "Publicar"}
                                            data-testid={`admin-toggle-${term.id}`}
                                        >
                                            {term.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                        </button>
                                        <button
                                            onClick={() => startEdit(term)}
                                            className="p-2 border border-[var(--line)] text-[var(--paper-2)] hover:border-[var(--danger)] hover:text-[var(--danger)]"
                                            data-testid={`admin-edit-${term.id}`}
                                        >
                                            <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => remove(term)}
                                            className="p-2 border border-[var(--danger)] text-[var(--danger)] hover:bg-[var(--danger)] hover:text-[var(--paper-2)]"
                                            data-testid={`admin-delete-${term.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </article>
                        ))}
                        {filtered.length === 0 && (
                            <p className="font-mono text-sm text-[var(--ink-muted)] py-10 text-center">
                                Sin términos en este filtro.
                            </p>
                        )}
                    </div>
                </div>
            </section>
        </>
    );
}
